/** Progress & Data — week activity, score breakdown, progress rows. */
import { SURFACE, TRACK, eyebrow, meta, Section } from '../shared';

// ─── Week activity ────────────────────────────────────────────────────────────

function WeekActivity() {
  const days = [
    { label: 'Mon', mins: 45 },
    { label: 'Tue', mins: 30 },
    { label: 'Wed', mins: 60 },
    { label: 'Thu', mins: 15 },
    { label: 'Fri', mins: 0 },
    { label: 'Sat', mins: 40 },
    { label: 'Sun', mins: 0 },
  ];
  const max = Math.max(...days.map((d) => d.mins), 1);

  return (
    <div style={{ ...SURFACE, padding: 20 }}>
      <div className="mb-4 flex items-baseline justify-between">
        <p style={eyebrow}>This Week</p>
        <span style={{ ...meta, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
          3h 10m total
        </span>
      </div>
      <div className="flex gap-2">
        {days.map((d) => {
          const intensity = d.mins / max;
          return (
            <div key={d.label} className="flex flex-1 flex-col items-center gap-2">
              <div className="flex w-full items-end justify-center" style={{ height: 52 }}>
                <div
                  className="w-full"
                  style={{
                    height: d.mins > 0 ? Math.max(6, intensity * 52) : 3,
                    borderRadius: 4,
                    background:
                      d.mins > 0 ? `rgba(155, 107, 218, ${0.18 + intensity * 0.55})` : TRACK,
                    transition: 'height 0.4s ease-out',
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: d.mins > 0 ? 'var(--color-text-secondary)' : 'var(--color-text-muted)',
                  letterSpacing: '0.02em',
                }}
              >
                {d.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Score breakdown ──────────────────────────────────────────────────────────

function ScoreBreakdown() {
  const segments = [
    { label: 'Correct', value: 142, color: 'var(--color-success)' },
    { label: 'Partial', value: 23, color: 'var(--color-warning)' },
    { label: 'Incorrect', value: 11, color: 'var(--color-error)' },
  ];
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  return (
    <div style={{ ...SURFACE, padding: 20 }}>
      <p style={{ ...eyebrow, marginBottom: 12 }}>Accuracy</p>
      <div
        className="flex overflow-hidden rounded-full"
        style={{ height: 6, gap: 2, marginBottom: 12 }}
      >
        {segments.map((seg) => (
          <div
            key={seg.label}
            className="rounded-full"
            style={{
              flex: seg.value / total,
              background: seg.color,
              transition: 'flex 0.5s ease-out',
            }}
          />
        ))}
      </div>
      <div className="flex gap-5">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center gap-2">
            <span
              className="rounded-full"
              style={{ width: 5, height: 5, background: seg.color, flexShrink: 0 }}
            />
            <span style={meta}>{seg.label}</span>
            <span style={{ ...meta, fontWeight: 600, color: 'var(--color-text)' }}>
              {seg.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Progress row ─────────────────────────────────────────────────────────────

function ProgressRow({
  label: text,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="flex items-center gap-3">
      <span className="shrink-0" style={{ ...meta, width: 96, color: 'var(--color-text)' }}>
        {text}
      </span>
      <div
        className="relative flex-1 overflow-hidden rounded-full"
        style={{ height: 4, background: TRACK }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span
        className="shrink-0 text-right"
        style={{ ...meta, width: 36, fontWeight: 600, color: 'var(--color-text-secondary)' }}
      >
        {value}/{max}
      </span>
    </div>
  );
}

// ─── Activity Heatmap ─────────────────────────────────────────────────────────

function ActivityHeatmap() {
  const data: number[][] = [
    [2, 0, 3, 1, 0, 4, 0],
    [1, 3, 0, 2, 1, 0, 3],
    [0, 2, 4, 0, 3, 1, 2],
    [3, 1, 0, 4, 2, 0, 1],
    [0, 0, 2, 3, 1, 4, 0],
    [4, 2, 1, 0, 0, 3, 2],
    [1, 0, 3, 2, 4, 0, 1],
    [0, 3, 0, 1, 2, 2, 4],
    [2, 1, 4, 0, 0, 3, 0],
    [3, 0, 2, 1, 3, 0, 2],
    [0, 4, 1, 3, 0, 2, 1],
    [1, 2, 0, 4, 1, 0, 3],
  ];

  const intensity = (v: number) => {
    if (v === 0) return TRACK;
    const alpha = 0.15 + (v / 4) * 0.6;
    return `rgba(155, 107, 218, ${alpha})`;
  };

  return (
    <div style={{ ...SURFACE, padding: 20 }}>
      <div className="mb-3 flex items-baseline justify-between">
        <p style={eyebrow}>Learning Activity</p>
        <span style={{ ...meta, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
          Last 12 weeks
        </span>
      </div>
      <div style={{ display: 'inline-grid', gridTemplateColumns: `repeat(12, 14px)`, gap: 3 }}>
        {data.map((week, wi) => (
          <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {week.map((v, di) => (
              <div
                key={di}
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  background: intensity(v),
                }}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        <span style={{ ...meta, fontSize: 10 }}>Less</span>
        {[0, 1, 2, 3, 4].map((v) => (
          <div
            key={v}
            style={{ width: 10, height: 10, borderRadius: 2, background: intensity(v) }}
          />
        ))}
        <span style={{ ...meta, fontSize: 10 }}>More</span>
      </div>
    </div>
  );
}

// ─── Skill Radar ──────────────────────────────────────────────────────────────

function SkillRadar() {
  const skills = [
    { name: 'Sorting', score: 92, color: 'var(--color-success)' },
    { name: 'Graph Theory', score: 78, color: 'var(--color-info)' },
    { name: 'Recursion', score: 65, color: 'var(--color-accent)' },
    { name: 'Dynamic Prog.', score: 45, color: 'var(--color-warning)' },
    { name: 'Bit Manipulation', score: 30, color: 'var(--color-error)' },
  ];

  return (
    <div style={{ ...SURFACE, padding: 20 }}>
      <p style={{ ...eyebrow, marginBottom: 16 }}>Strengths &amp; Weaknesses</p>
      <div className="flex flex-col gap-4">
        {skills.map((s) => (
          <div key={s.name} className="flex items-center gap-3">
            <span
              className="shrink-0"
              style={{ ...meta, width: 110, color: 'var(--color-text)', fontWeight: 500 }}
            >
              {s.name}
            </span>
            <div
              className="relative flex-1 overflow-hidden rounded-full"
              style={{ height: 6, background: TRACK }}
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  width: `${s.score}%`,
                  background: s.color,
                  transition: 'width 0.6s ease-out',
                }}
              />
            </div>
            <span
              className="shrink-0"
              style={{
                ...meta,
                width: 32,
                textAlign: 'right',
                fontWeight: 600,
                color: 'var(--color-text)',
              }}
            >
              {s.score}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Stat Ring ────────────────────────────────────────────────────────────────

function StatRing({
  value,
  max,
  label: text,
  color,
}: {
  value: number;
  max: number;
  label: string;
  color: string;
}) {
  const r = 16;
  const circumference = 2 * Math.PI * r;
  const pct = Math.min(100, (value / max) * 100);
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="flex items-center gap-3" style={{ flex: '1 1 0', minWidth: 0 }}>
      <div className="relative shrink-0" style={{ width: 40, height: 40 }}>
        <svg width="40" height="40" viewBox="0 0 40 40" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="20" cy="20" r={r} fill="none" stroke={TRACK} strokeWidth="3" />
          <circle
            cx="20"
            cy="20"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ fontSize: 12, fontWeight: 700, color, letterSpacing: '-0.02em' }}
        >
          {Math.round(pct)}
        </div>
      </div>
      <div className="min-w-0">
        <p style={{ ...meta, fontWeight: 600, color: 'var(--color-text)' }}>
          {value}/{max}
        </p>
        <p style={{ ...meta, fontSize: 11 }}>{text}</p>
      </div>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

export default function ProgressTab() {
  return (
    <>
      <Section label="Week Activity">
        <WeekActivity />
      </Section>

      <Section label="Score Breakdown">
        <ScoreBreakdown />
      </Section>

      <Section label="Course Progress">
        <div style={{ ...SURFACE, padding: 20 }}>
          <p style={{ ...eyebrow, marginBottom: 14 }}>Course Progress</p>
          <div className="flex flex-col gap-4">
            <ProgressRow label="Algorithms" value={8} max={12} color="var(--color-info)" />
            <ProgressRow label="Chemistry" value={5} max={8} color="var(--color-success)" />
            <ProgressRow label="Algebra" value={3} max={15} color="var(--color-accent)" />
            <ProgressRow label="Economics" value={10} max={10} color="var(--color-warning)" />
          </div>
        </div>
      </Section>

      <Section label="Activity Heatmap">
        <ActivityHeatmap />
      </Section>

      <Section label="Skill Breakdown">
        <SkillRadar />
      </Section>

      <Section label="Stat Rings">
        <div style={{ ...SURFACE, padding: 20 }}>
          <div className="flex gap-4">
            <StatRing value={8} max={12} label="Lessons done" color="var(--color-accent)" />
            <StatRing value={42} max={60} label="Steps done" color="var(--color-info)" />
            <StatRing value={87} max={100} label="Accuracy" color="var(--color-success)" />
            <StatRing value={4} max={7} label="Streak days" color="var(--color-warning)" />
          </div>
        </div>
      </Section>
    </>
  );
}
